import { useMemo, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { TableTitleBox } from '../../common/components';
import MovementDialog from '../bank-movements/components/MovementDialog';
import { useMovementTypes } from '../../hooks/useMovementTypes';
import { useBankMovementsByDateRange } from '../../hooks/useBankMovements';
import CashRetentionsFilters from './components/CashRetentionsFilters';
import CashRetentionsTable from './components/CashRetentionsTable';
import {
  CASH_RETENTIONS_DEFAULT_FILTERS,
  CASH_RETENTIONS_ITEMS_PER_PAGE,
} from './constants/cashRetentionsConstants';
import { exportCashRetentionsToExcel } from './utils/exportExcel';

const CashRetentions = () => {
  const { data: movementTypes = [] } = useMovementTypes();
  const allowedMovementTypes = useMemo(
    () => movementTypes.filter((type) => type.is_bank_movement_type === false),
    [movementTypes]
  );

  const [filters, setFilters] = useState(CASH_RETENTIONS_DEFAULT_FILTERS);
  const [searchTerm, setSearchTerm] = useState('');
  const [reconciliationFilter, setReconciliationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(CASH_RETENTIONS_ITEMS_PER_PAGE);
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);

  const queryFilters = useMemo(
    () => ({
      start_date: filters.startDate,
      end_date: filters.endDate,
    }),
    [filters.startDate, filters.endDate]
  );

  const {
    data: movements = [],
    isLoading,
    refetch,
  } = useBankMovementsByDateRange(queryFilters, {
    enabled: Boolean(filters.startDate && filters.endDate),
  });

  const cashMovements = useMemo(
    () => (movements || []).filter((movement) => !movement.uuid_bank_account),
    [movements]
  );

  const filteredData = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return cashMovements.filter((movement) => {
      const matchesSearch =
        !normalizedSearch ||
        movement.concept?.toLowerCase().includes(normalizedSearch) ||
        movement.voucher_number?.toLowerCase().includes(normalizedSearch) ||
        movement.legal_entity_name?.toLowerCase().includes(normalizedSearch);

      const matchesReconciliation =
        reconciliationFilter === 'all' ||
        (reconciliationFilter === 'reconciled' && movement.is_reconciled) ||
        (reconciliationFilter === 'pending' && !movement.is_reconciled);

      const matchesType = typeFilter === 'all' || movement.uuid_movement_type === typeFilter;

      return matchesSearch && matchesReconciliation && matchesType;
    });
  }, [cashMovements, reconciliationFilter, searchTerm, typeFilter]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage) || 1;
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleExport = async () => {
    await exportCashRetentionsToExcel(filteredData);
  };

  const handleEditMovement = useCallback((movement) => {
    setSelectedMovement(movement);
    setMovementDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setMovementDialogOpen(false);
    setSelectedMovement(null);
    refetch();
  }, [refetch]);

  return (
    <Box>
      <TableTitleBox title="Bancos | Retenciones y Efectivo" />

      <CashRetentionsFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        searchTerm={searchTerm}
        onSearchChange={(value) => {
          setSearchTerm(value);
          setPage(1);
        }}
        reconciliationFilter={reconciliationFilter}
        onReconciliationChange={(value) => {
          setReconciliationFilter(value);
          setPage(1);
        }}
        typeFilter={typeFilter}
        onTypeChange={(value) => {
          setTypeFilter(value);
          setPage(1);
        }}
        allowedMovementTypes={allowedMovementTypes}
        onExport={handleExport}
      />

      <CashRetentionsTable
        movements={paginatedData}
        totalItems={filteredData.length}
        page={safePage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, value) => setPage(value)}
        onRowsPerPageChange={handleRowsPerPageChange}
        isLoading={isLoading}
        onEditMovement={handleEditMovement}
        onRefresh={refetch}
      />

      <MovementDialog
        open={movementDialogOpen}
        onClose={handleCloseDialog}
        movement={selectedMovement}
        account={null}
        editMode
      />
    </Box>
  );
};

export default CashRetentions;

